// État global du client + connexion WebSocket (reconnexion avec délai croissant, ping toutes les 25 s,
// reconnexion au retour au premier plan — iOS coupe les sockets en arrière-plan).
import { useEffect, useState } from "preact/hooks";
import type { ClientMessage, ClientView, DiceRollRecord, ServerMessage } from "../shared/types";
import { removeSession, setLastSessionCode, type Session } from "./api";

export interface DiceShow { id: number; rolls: DiceRollRecord[]; actorName: string }
export interface Toast { id: number; text: string; tone: "info" | "succes" | "danger" }

export interface AppState {
  session: Session | null;
  view: ClientView | null;
  connection: "deconnecte" | "connexion" | "connecte";
  gm: { busy: boolean; label: string };
  dice: DiceShow[];          // file d'animations de dés à jouer
  toasts: Toast[];
  error: string | null;
}

const PING_MS = 25_000;
/** Aucun message du serveur (pong compris) depuis ce délai : la socket est morte, on la refait. */
const STALE_MS = 75_000;
const CONNECT_TIMEOUT_MS = 12_000;
const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 30_000;
const TOAST_MS = 4_500;
const MAX_TOASTS = 4;
/** Fermetures du serveur : jeton inconnu (4401) ou monde disparu (4404). Le monde a oublié ce joueur. */
const CLOSE_SESSION_UNKNOWN = 4401;
const CLOSE_WORLD_GONE = 4404;
const MSG_FORGOTTEN = "Ce monde t’a oublié : rejoins-le à nouveau avec son code.";

type Timer = ReturnType<typeof setTimeout>;

let state: AppState = {
  session: null,
  view: null,
  connection: "deconnecte",
  gm: { busy: false, label: "" },
  dice: [],
  toasts: [],
  error: null,
};

const listeners = new Set<() => void>();
let socket: WebSocket | null = null;
let retryTimer: Timer | null = null;
let connectTimer: Timer | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let retryDelay = RETRY_MIN_MS;
let lastInboundAt = 0;
/** Fermeture définitive décidée par le serveur (code 4xxx) : pas de relance automatique. */
let fatal = false;
/** Dernier message « error » affiché sur la socket courante : la raison de fermeture ne le répète pas. */
let announced: string | null = null;
let diceSeq = 0;
let toastSeq = 0;
const toastTimers = new Map<number, Timer>();

// ───────── état et abonnés ─────────

/** Remplace les champs donnés (l'objet d'état est recréé à chaque changement) et prévient les abonnés. */
function update(patch: Partial<AppState>): void {
  state = { ...state, ...patch };
  for (const fn of [...listeners]) fn();
}

export function getState(): AppState {
  return state;
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Hook Preact : re-rend le composant à chaque changement d'état. */
export function useAppState(): AppState {
  const [, setTick] = useState(0);
  const rendered = state;
  useEffect(() => {
    const off = subscribe(() => setTick((n) => n + 1));
    // un changement survenu entre le rendu et l'abonnement ne doit pas être perdu
    if (state !== rendered) setTick((n) => n + 1);
    return off;
  }, []);
  return rendered;
}

// ───────── connexion ─────────

export function connect(session: Session): void {
  const prev = state.session;
  const same = prev !== null && prev.code === session.code && prev.token === session.token;
  setLastSessionCode(session.code);
  if (same && socket && socket.readyState !== WebSocket.CLOSING && socket.readyState !== WebSocket.CLOSED) return;
  stopRetry();
  dropSocket();
  fatal = false;
  retryDelay = RETRY_MIN_MS;
  update({
    session,
    view: same ? state.view : null,
    connection: "connexion",
    gm: same ? state.gm : { busy: false, label: "" },
    dice: same ? state.dice : [],
    error: null,
  });
  startPing();
  openSocket();
}

/** Quitte le monde : ferme la socket et oublie la session courante (la liste sauvegardée reste intacte). */
export function disconnect(): void {
  stopRetry();
  stopPing();
  dropSocket();
  fatal = false;
  setLastSessionCode(null);
  update({ session: null, view: null, connection: "deconnecte", gm: { busy: false, label: "" }, dice: [], error: null });
}

export function send(msg: ClientMessage): void {
  const ws = socket;
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(msg));
    return;
  }
  pushToast("Connexion perdue, nouvel essai…", "danger");
  revive();
}

function socketUrl(s: Session): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}/api/ws?code=${encodeURIComponent(s.code)}&token=${encodeURIComponent(s.token)}`;
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function openSocket(): void {
  const session = state.session;
  dropSocket();
  if (!session || fatal) return;
  if (isOffline()) {
    update({ connection: "deconnecte" }); // la reprise se fera à l'événement « online »
    return;
  }
  if (state.connection !== "connexion") update({ connection: "connexion" });
  announced = null;
  let ws: WebSocket;
  try {
    ws = new WebSocket(socketUrl(session));
  } catch {
    update({ connection: "deconnecte" });
    scheduleRetry();
    return;
  }
  socket = ws;
  armConnectTimeout(ws);
  ws.onopen = () => {
    if (socket !== ws) return;
    clearConnectTimeout();
    retryDelay = RETRY_MIN_MS;
    lastInboundAt = Date.now();
    update({ connection: "connecte", error: null });
  };
  ws.onmessage = (ev: MessageEvent) => {
    if (socket !== ws) return;
    lastInboundAt = Date.now();
    handleMessage(ev.data);
  };
  ws.onclose = (ev: CloseEvent) => {
    if (socket !== ws) return;
    socket = null;
    onLost(ev.code, ev.reason);
  };
}

/** Ferme la socket courante sans que ses gestionnaires ne se déclenchent. */
function dropSocket(): void {
  clearConnectTimeout();
  const ws = socket;
  socket = null;
  if (!ws) return;
  ws.onopen = null;
  ws.onmessage = null;
  ws.onclose = null;
  try {
    ws.close();
  } catch {
    // déjà fermée
  }
}

/** Socket tombée (ou jamais ouverte) : on relance avec un délai croissant, sauf refus définitif du serveur. */
function onLost(code: number, reason: string): void {
  clearConnectTimeout();
  const session = state.session;
  if (!session) return;
  if (code === CLOSE_SESSION_UNKNOWN || code === CLOSE_WORLD_GONE) {
    forgetWorld(session.code);
    return;
  }
  if (code >= 4000 && code <= 4999) {
    const message = reason || "Le monde n’accepte plus cette connexion.";
    fatal = true;
    stopRetry();
    update({ connection: "deconnecte", error: message });
    // le serveur a déjà affiché ce motif (message « error ») juste avant la fermeture
    if (message !== announced) pushToast(message, "danger");
    return;
  }
  update({ connection: "deconnecte" });
  scheduleRetry();
}

/**
 * Le monde ne reconnaît plus cette session : aucune relance, la session sauvegardée est retirée et l'accueil
 * s'ouvre avec le toast d'oubli. Le motif du serveur, déjà affiché par son message « error », n'est pas répété.
 */
function forgetWorld(code: string): void {
  stopRetry();
  stopPing();
  dropSocket();
  removeSession(code);
  update({ session: null, view: null, connection: "deconnecte", gm: { busy: false, label: "" }, dice: [], error: null });
  pushToast(MSG_FORGOTTEN, "danger");
}

function scheduleRetry(): void {
  stopRetry();
  if (!state.session || fatal || isOffline()) return;
  const delay = retryDelay;
  retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    openSocket();
  }, delay);
}

function stopRetry(): void {
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = null;
}

/** Une socket qui ne s'ouvre pas dans le délai (réseau mou) est abandonnée et relancée. */
function armConnectTimeout(ws: WebSocket): void {
  clearConnectTimeout();
  connectTimer = setTimeout(() => {
    connectTimer = null;
    if (socket !== ws || ws.readyState !== WebSocket.CONNECTING) return;
    dropSocket();
    update({ connection: "deconnecte" });
    scheduleRetry();
  }, CONNECT_TIMEOUT_MS);
}

function clearConnectTimeout(): void {
  if (connectTimer !== null) clearTimeout(connectTimer);
  connectTimer = null;
}

/** Relance tout de suite si la connexion est tombée ou semble morte (retour au premier plan, réseau revenu). */
function revive(): void {
  if (!state.session || fatal) return;
  const ws = socket;
  const healthy = ws !== null && (
    ws.readyState === WebSocket.CONNECTING ||
    (ws.readyState === WebSocket.OPEN && Date.now() - lastInboundAt <= STALE_MS)
  );
  if (healthy) return;
  retryDelay = RETRY_MIN_MS;
  stopRetry();
  openSocket();
}

function onPhone(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
}

/**
 * Téléphone : la page cachée va être gelée. Fermer la socket tout de suite dit au serveur que le joueur n'est plus là,
 * et qu'il faut passer aux notifications. Le retour au premier plan rouvre tout (revive).
 */
function sleepSocket(): void {
  if (!state.session || fatal) return;
  stopRetry();
  dropSocket();
}

function startPing(): void {
  stopPing();
  pingTimer = setInterval(pingTick, PING_MS);
}

function stopPing(): void {
  if (pingTimer !== null) clearInterval(pingTimer);
  pingTimer = null;
}

function pingTick(): void {
  const ws = socket;
  if (!state.session || fatal || !ws || ws.readyState !== WebSocket.OPEN) return;
  if (Date.now() - lastInboundAt > STALE_MS) {
    dropSocket();
    retryDelay = RETRY_MIN_MS;
    update({ connection: "deconnecte" });
    scheduleRetry();
    return;
  }
  ws.send(JSON.stringify({ t: "ping" }));
}

function handleMessage(raw: unknown): void {
  if (typeof raw !== "string") return;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return;
  }
  if (typeof data !== "object" || data === null) return;
  const msg = data as ServerMessage;
  switch (msg.t) {
    case "view":
      update({ view: msg.view, gm: { busy: msg.view.gmBusy, label: msg.view.gmBusy ? state.gm.label : "" } });
      break;
    case "dice":
      if (Array.isArray(msg.rolls) && msg.rolls.length > 0) {
        update({ dice: [...state.dice, { id: ++diceSeq, rolls: msg.rolls, actorName: msg.actorName }] });
      }
      break;
    case "gm_busy":
      update({ gm: { busy: msg.busy, label: msg.label } });
      break;
    case "toast":
      pushToast(msg.text, msg.tone);
      break;
    case "error":
      announced = msg.message;
      pushToast(msg.message, "danger");
      break;
    case "pong":
      break;
  }
}

// ───────── toasts et dés ─────────

export function pushToast(text: string, tone: Toast["tone"] = "info"): void {
  const id = ++toastSeq;
  const all: Toast[] = [...state.toasts, { id, text, tone }];
  const kept = all.slice(-MAX_TOASTS);
  for (const dropped of all.slice(0, all.length - kept.length)) forgetToastTimer(dropped.id);
  toastTimers.set(id, setTimeout(() => dismissToast(id), TOAST_MS));
  update({ toasts: kept });
}

export function dismissToast(id: number): void {
  forgetToastTimer(id);
  if (state.toasts.some((t) => t.id === id)) update({ toasts: state.toasts.filter((t) => t.id !== id) });
}

function forgetToastTimer(id: number): void {
  const timer = toastTimers.get(id);
  if (timer === undefined) return;
  clearTimeout(timer);
  toastTimers.delete(id);
}

export function shiftDice(): void {
  if (state.dice.length === 0) return;
  update({ dice: state.dice.slice(1) });
}

// ───────── événements du navigateur ─────────

if (typeof window !== "undefined") {
  window.addEventListener("online", () => revive());
  window.addEventListener("offline", () => {
    if (!state.session) return;
    stopRetry();
    dropSocket();
    update({ connection: "deconnecte" });
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") revive();
    else if (onPhone()) sleepSocket();
  });
}
